// Single source of truth for the white-label build flag. Vite inlines
// `import.meta.env.VITE_WHITE_LABEL` at build time. Normalises whitespace
// and casing so a stray "true " in .env still evaluates correctly, and
// defaults to false when the variable is missing entirely.
export const isWhiteLabel = () => {
  const raw = import.meta.env?.VITE_WHITE_LABEL;
  if (raw === true) return true;
  if (typeof raw !== "string") return false;
  return raw.trim().toLowerCase() === "true";
};

// White label mock test title constant
export const WHITE_LABEL_MOCK_TITLE = "PTEMax";

// White label default logo path constant
export const WHITE_LABEL_DEFAULT_LOGO_PATH = "/images/web-new-images/ptemax-icon.svg";

// Resolve the partner subdomain from the current host. Shape is
// `[www.]<subdomain>.<base>`, where <base> is "localhost" in dev or
// "<domain>.<tld>" in prod — e.g. www.softude.localhost → "softude",
// softude.20task.com → "softude". A leading "www" is stripped (so login and
// every other request send "softude", never "www"), and the partner label is
// returned only when one actually sits in front of the base domain; otherwise
// null (no partner subdomain → callers should send nothing).
// Number of trailing labels that make up the base domain, i.e. everything that
// is NOT a partner label. "localhost" is 1; a two-level public suffix such as
// languageacademy.com.au is 3; everything else is 2.
// ponytail: explicit suffix list rather than a public-suffix-list dependency —
// extend it if a partner is ever hosted under another multi-label TLD.
const TWO_LEVEL_SUFFIXES = ["com.au", "net.au", "org.au", "co.uk", "co.nz", "co.in"];

const baseDomainLength = (parts) => {
  if (parts[parts.length - 1] === "localhost") return 1;
  const lastTwo = parts.slice(-2).join(".");
  return TWO_LEVEL_SUFFIXES.includes(lastTwo) ? 3 : 2;
};

export const getSubdomain = () => {
  if (typeof window === "undefined" || !window.location) return null;
  const parts = window.location.hostname.split(".");
  if (parts[0] === "www") parts.shift();
  return parts.length > baseDomainLength(parts) ? parts[0] : null;
};

// Rewrite a sibling-app URL (VITE_LA_URL) onto the current partner's host, so
// a logged-out partner student is bounced to their own institute's login
// instead of the Language Academy one. E.g. on ptestudyempire.localhost:3001,
// http://www.localhost:3000 → http://ptestudyempire.localhost:3000.
// Returns the URL untouched off a partner subdomain, on an LA build, or if it
// fails to parse.
export const toPartnerHost = (url) => {
  const subdomain = getSubdomain();
  if (!isWhiteLabel() || !subdomain || !url) return url;
  try {
    const parsed = new URL(url);
    const parts = parsed.hostname.split(".");
    if (parts[0] === "www") parts.shift();
    // Drop an existing partner label so we swap rather than stack them.
    if (parts.length > baseDomainLength(parts)) parts.shift();
    parsed.hostname = [subdomain, ...parts].join(".");
    return parsed.toString().replace(/\/$/, "");
  } catch {
    return url;
  }
};
